import { useApolloClient } from '@apollo/client';
import { useEffect, useState } from 'react';

import { gengql } from '@/__generated__';
import Link from '@/components/Link';
import { groupBy } from '@/utils';

import s from './InvitationSummary.module.scss';

const GET_USER = gengql(/* GraphQL */ `
  query getInvitationSummaryUser($id: String!) {
    user(id: $id) {
      id
    }
  }
`);

interface InvitationSummaryProps {
  profiles: any[];
  onInvitationExampleClick: any;
}

const InvitationSummary = ({
  profiles,
  onInvitationExampleClick,
}: InvitationSummaryProps): JSX.Element => {
  const client = useApolloClient();
  const [users, setUsers] = useState<Record<string, any>>({});
  useEffect(() => {
    if (!profiles.length) return;

    const fetchUsers = async () => {
      const results = await Promise.all(
        profiles.map((p) =>
          client
            .query({
              query: GET_USER,
              variables: { id: p.url_endpoint },
            })
            .then((r) => ({ url_endpoint: p.url_endpoint, user: r.data?.user }))
        )
      );

      const map: Record<string, any> = {};
      results.forEach(({ url_endpoint, user }) => {
        if (user) {
          map[url_endpoint] = user;
        }
      });
      setUsers(map);
    };

    fetchUsers();
  }, [profiles, client]);

  const registered = profiles.filter((p: any) => users[p.url_endpoint]);
  const unregistered = profiles.filter((p: any) => !users[p.url_endpoint] && !fromIntegration(p));
  const unregisteredFromIntegration = profiles.filter(
    (p: any) => !users[p.url_endpoint] && fromIntegration(p)
  );
  const unregisteredByAgent = groupBy(unregisteredFromIntegration, (p) => p.sources[0].agent.name);

  const pluralizeExpert = (list: any) => (list.length === 1 ? 'expert' : 'experts');

  return (
    <div className={s.profileSummary}>
      <div>Invitation will be sent to:</div>
      {registered.length > 0 && (
        <div className={s.profileSummaryType}>
          {registered.length} registered {pluralizeExpert(registered)}
          &nbsp;
          <Link onClick={() => onInvitationExampleClick(registered[0])} className="underline">
            (show example)
          </Link>
        </div>
      )}
      {unregistered.length > 0 && (
        <div className={s.profileSummaryType}>
          {unregistered.length} unregistered {pluralizeExpert(unregistered)}
          &nbsp;
          <Link onClick={() => onInvitationExampleClick(unregistered[0])} className="underline">
            (show example)
          </Link>
        </div>
      )}
      {Object.keys(unregisteredByAgent).map((agent) => (
        <div className={s.profileSummaryType} key={agent}>
          {unregisteredByAgent[agent].length} unregistered
          {pluralizeExpert(unregisteredByAgent[agent])} from {agent}
          &nbsp;
          <Link
            onClick={() => onInvitationExampleClick(unregisteredByAgent[agent][0])}
            className="underline"
          >
            (show example)
          </Link>
        </div>
      ))}
    </div>
  );
};

function fromIntegration(p: any) {
  return (
    p.sources &&
    p.sources[0] &&
    p.sources[0].agent &&
    !!p.sources[0].agent.custom_candidate_invitation_message
  );
}

export default InvitationSummary;
